import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { api } from '../lib/api';
import { Instagram, Facebook, MessageCircle, Users } from 'lucide-react';
import { ExpandableText } from '../components/ExpandableText';

export default function Team() {
  const [team, setTeam] = useState(null);
  useEffect(() => { api.get('/team').then(({ data }) => setTeam(data || [])).catch(() => setTeam([])); }, []);

  return (
    <div className="pt-24 pb-20">
      <section className="relative">
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">Our Team</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">The instructors behind every safe, brilliant dive.</h1>
          <p className="mt-5 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">Our instructors hold the highest levels of qualification and experience. Meet the people who will guide you under the surface.</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {team === null && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
              ))}
            </div>
          )}
          {team && team.length === 0 && (
            <div data-testid="team-empty-state" className="rounded-3xl border border-white/10 bg-white/5 p-12 sm:p-16 text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-[#00b4d8]/15 text-[#00b4d8] inline-flex items-center justify-center mb-5"><Users className="h-6 w-6" /></div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold">Team profiles coming soon</h2>
              <p className="mt-3 text-white/60 max-w-md mx-auto">We’re updating our instructor bios. Check back shortly to meet the team.</p>
            </div>
          )}
          {team && team.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {team.map((m) => (
                <Card data-testid="team-member-card" key={m.id} className="group rounded-3xl border-white/10 bg-white/5 overflow-hidden flex flex-col">
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#0d223d]">
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={m.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-white/40"><Users className="h-12 w-12" /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/20 to-transparent" />
                  </div>
                  <div className="p-5 flex flex-col gap-2 flex-1">
                    <div className="font-display text-xl font-semibold">{m.name}</div>
                    {m.title && <div className="text-xs uppercase tracking-[0.18em] text-[#00b4d8]">{m.title}</div>}
                    {m.bio && (
                      <div className="mt-2">
                        <ExpandableText
                          text={m.bio}
                          limit={320}
                          testId={`team-bio-${m.id}`}
                        />
                      </div>
                    )}
                    <div className="mt-auto pt-3 flex items-center gap-3">
                      {m.instagram_url && <a href={m.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-[#00b4d8] hover:text-[#00b4d8]"><Instagram className="h-4 w-4" /></a>}
                      {m.facebook_url && <a href={m.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-[#00b4d8] hover:text-[#00b4d8]"><Facebook className="h-4 w-4" /></a>}
                      {m.whatsapp_url && <a href={m.whatsapp_url} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-[#c8e600] hover:text-[#c8e600]"><MessageCircle className="h-4 w-4" /></a>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
